import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { SlidersHorizontal, Save, RotateCcw, Info } from "lucide-react";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { usePlatformSettings } from "@/api/queries/platformSettings";
import { usePatchPlatformSettings } from "@/api/mutations/platformSettings";
import type { PlatformSettings } from "@/types";

// #235: Plattform-weite Policy-/Default-Knöpfe (admin-only). DB ist die Wahrheit; die
// Konsumenten (Budget-Gate, Marketplace-Lockdown, Quoten, Token-TTLs, …) lesen die
// effektiven Werte LIVE — eine Änderung hier wirkt ab dem nächsten Turn, ohne Redeploy.
// Jedes Feld hat einen Info-Text (Icon-Tooltip + Beschreibung), was es bewirkt.

const INPUT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-base px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-base disabled:cursor-not-allowed disabled:opacity-50";

// ---- Feld-Deskriptoren (eine Quelle für Render + Diff) ----
type StringArrayKey = "brainImageAllowedRegistries" | "attachmentMimeWhitelist" | "settingsResolveDenylist";
type Field =
  | { key: keyof PlatformSettings; kind: "bool"; label: string; info: string }
  | { key: keyof PlatformSettings; kind: "number"; label: string; info: string; min?: number }
  | { key: keyof PlatformSettings; kind: "text"; label: string; info: string }
  | { key: StringArrayKey; kind: "stringArray"; label: string; info: string; placeholder?: string }
  // #P002 (UC-1): EIN Setting, dessen Wert ein Objekt aus dem festen Modell-Vokabular ist. Ein
  // eigener `kind` statt zweier Deskriptoren mit verschachtelten Schlüsseln: der generische
  // Diff und `set()` arbeiten auf Top-Level-Keys, und der Master ersetzt den Wert als GANZES
  // (ein Teil-Objekt lehnt er ab). Eine Vokabular-Erweiterung ist hier ein Feld mehr.
  | { key: "anthropicModelMeta"; kind: "modelMeta"; label: string; info: string }
  | { key: keyof PlatformSettings; kind: "enum"; label: string; info: string; options: { value: string; label: string }[] };

type Group = { title: string; fields: Field[] };

const GROUPS: Group[] = [
  {
    title: "Marktplätze & Connectors",
    fields: [
      { key: "strictMarketplaces", kind: "bool", label: "Marktplatz-Lockdown (strict)", info: "Erlaubt nur admin-kuratierte Plugin-Marktplätze. Aktiv = Agenten und User können keine eigenen Marktplätze hinzufügen (sicher per Default)." },
      { key: "marketplaceFetchAllowInsecure", kind: "bool", label: "Unsichere Marktplatz-Quellen erlauben", info: "Erlaubt das Laden von Marktplatz-Daten über unverschlüsseltes http statt nur https. Nur für Test/Intranet." },
      { key: "connectorOauthAllowInsecure", kind: "bool", label: "Unsicheres Connector-OAuth erlauben", info: "Erlaubt Connector-OAuth über http/localhost statt nur https. Nur für Entwicklung." },
    ],
  },
  {
    title: "Budget",
    fields: [
      { key: "budgetDefaultAgentUsd", kind: "number", label: "Default-Limit Agent (USD/Monat)", info: "Monatliches Kostenlimit pro Agent, das greift, wenn der Agent kein eigenes Limit hat. Bei Überschreitung werden weitere Turns blockiert." },
      { key: "budgetDefaultUserUsd", kind: "number", label: "Default-Limit User (USD/Monat)", info: "Monatliches Kostenlimit pro User (Summe seiner Agenten), das greift, wenn der User kein eigenes Limit hat." },
      { key: "budgetResetTz", kind: "text", label: "Budget-Reset-Zeitzone", info: "Zeitzone für den Monatswechsel des Budgets (Auto-Reset zum Monatsersten). IANA-Format, z.B. Europe/Zurich." },
      { key: "turnBudgetDefaultUsd", kind: "number", label: "Default Per-Turn-Cap (USD)", info: "Kosten-Obergrenze pro einzelnem Turn (Runaway-Schutz), die greift, wenn ein Agent keinen eigenen Wert hat. Bricht einen Turn ab, wenn seine geschätzten Kosten den Wert überschreiten. 0 = kein Cap. Agenten können pro Agent überschrieben werden." },
    ],
  },
  {
    title: "Sessions",
    fields: [
      { key: "sessionDefaultModel", kind: "text", label: "Default-Modell neuer Sessions", info: "Claude-Modell, mit dem eine neue Session startet, bis Owner oder Admin es ändert. z.B. default / sonnet / haiku." },
      { key: "caseDefaultModel", kind: "text", label: "Default-Modell neuer Prozess-Läufe", info: "Claude-Modell, mit dem ein neuer Prozess-Lauf startet, wenn weder die Einrichtung noch der Agent etwas anderes vorgibt. Getrennt vom Chat-Default, weil ein Lauf unbeaufsichtigt viele Turns macht. z.B. default / sonnet / haiku." },
      { key: "sessionDefaultCwd", kind: "text", label: "Default-Arbeitsverzeichnis", info: "Verzeichnis, in dem eine neue Session startet — der Ordner, in dem der Agent arbeitet." },
    ],
  },
  {
    title: "Anthropic-Modelle — was sie dürfen",
    fields: [
      {
        key: "anthropicModelMeta",
        kind: "modelMeta",
        label: "Eigenschaften der Anthropic-Modelle",
        info: "Gilt für ALLE Anthropic-Modelle (default/sonnet/haiku/opus) gemeinsam — nicht pro Modell und nicht pro Agent. Prozesse können daran Anforderungen stellen („nur lokal“, „darf firmen-heikle Daten verarbeiten“) und bekommen dann nur passende Modelle zur Auswahl. Standard: Cloud, nicht für heikle Daten freigegeben. Wer Anthropic für heikle Daten freigibt (z.B. mit einem Vertrag ohne Datenspeicherung), stellt es hier plattformweit um — eine Aussage des Betreibers, kein technischer Nachweis. Die Modelle eines eigenen Gateways (Slots) werden dagegen je Agent beim Slot gesetzt.",
      },
    ],
  },
  {
    title: "Brain-Images",
    fields: [
      { key: "brainImageAllowedRegistries", kind: "stringArray", label: "Erlaubte Registries", info: "Container-Registries, aus denen Brain-Images stammen dürfen (eine pro Zeile). Leer = alle erlaubt. Schützt vor nicht vertrauenswürdigen Images.", placeholder: "ghcr.io" },
    ],
  },
  {
    title: "Uploads",
    fields: [
      { key: "attachmentMaxBytes", kind: "number", label: "Max. Upload-Größe (Bytes)", info: "Maximale Größe eines Datei-Uploads in Bytes. 0 = unbegrenzt." },
      { key: "attachmentMimeWhitelist", kind: "stringArray", label: "Erlaubte Datei-Typen (MIME)", info: "Erlaubte MIME-Typen für Uploads (einer pro Zeile), z.B. image/png. Leer = alle erlaubt.", placeholder: "image/png" },
    ],
  },
  {
    title: "Quoten & Verhalten",
    fields: [
      { key: "scheduledTasksMaxPerAgent", kind: "number", label: "Max. geplante Tasks pro Agent", info: "Maximale Anzahl gleichzeitig geplanter Aufgaben pro Agent.", min: 1 },
      { key: "connectionMaxPendingOutbound", kind: "number", label: "Max. offene Verbindungsanfragen", info: "Maximale Anzahl offener ausgehender Agent-zu-Agent-Verbindungsanfragen pro Agent (Schutz vor Flutung).", min: 1 },
      { key: "connectorAccessRequiresApproval", kind: "bool", label: "Connector-Nutzung immer freigeben lassen", info: "Aus: Connectoren ohne Verantwortlichen (\u201eSystem\u201c) aktiviert der Agent-Owner selbst \u2014 wie bisher. An: jede Aktivierung braucht eine Freigabe; bei System-Connectoren entscheidet jeder Admin \u00fcber die Anfragen-Liste (kein Push). Wirkt nur auf neue Aktivierungen \u2014 bereits aktive Connectoren bleiben aktiv." },
      { key: "connectorAccessMaxPending", kind: "number", label: "Max. offene Connector-Anfragen", info: "Maximale Anzahl offener Zugriffs-Anfragen pro Antragsteller (Schutz vor Anfragen-Flut beim Verantwortlichen). Getrennt von der Verbindungsanfragen-Quote.", min: 1 },
      { key: "masterDefaultStopBehavior", kind: "enum", label: "Default /stop-Verhalten", info: "Was der Befehl /stop standardmäßig beendet: alle wartenden Vorgänge des Agents oder nur den gerade laufenden.", options: [{ value: "all", label: "Alle (all)" }, { value: "current", label: "Nur aktuellen (current)" }] },
    ],
  },
  {
    title: "Sicherheit — Token-Laufzeiten & Timeouts",
    fields: [
      { key: "jwtTtlSeconds", kind: "number", label: "Login-Token-Laufzeit (Sek.)", info: "Gültigkeitsdauer eines Admin-/User-Login-Tokens in Sekunden. Kürzer = sicherer, aber häufigeres Neu-Anmelden.", min: 1 },
      { key: "natsJwtDefaultTtlSeconds", kind: "number", label: "Messaging-Token-Laufzeit (Sek.)", info: "Gültigkeitsdauer der Messaging-Zugangstoken von Agenten und Channels in Sekunden. Sicherheitskritisch — kurz halten (ein verwaister Agent verliert binnen dieser Zeit den Zugang).", min: 1 },
      { key: "permissionTimeoutMs", kind: "number", label: "Genehmigungs-Timeout (ms)", info: "Wie lange auf eine Genehmigungs-Antwort gewartet wird, bevor automatisch abgelehnt wird (Millisekunden).", min: 1 },
    ],
  },
  {
    title: "Prozess-Engine",
    fields: [
      { key: "caseDispatchTimeoutMinutes", kind: "number", label: "Dispatch-Frist pro Prozess-Schritt (Minuten)", info: "Wie lange ein zugestellter Prozess-Schritt gültig bleibt (Wartezeit in der Warteschlange + Turn-Dauer). Abgelaufene Zustellungen werden verworfen und neu angestoßen.", min: 1 },
      { key: "caseStuckTimeoutMinutes", kind: "number", label: "Frist für stehende Läufe (Minuten)", info: "Wie lange ein Lauf ohne Fortschritt stehen darf, bevor er blockiert wird und der Inhaber eine Meldung bekommt. Greift, wenn ein Schritt dauerhaft nicht zugestellt werden kann — etwa weil der Agent offline bleibt oder das Budget erschöpft ist.", min: 1 },
    ],
  },
  {
    title: "Settings-Auslesen",
    fields: [
      { key: "settingsResolveDenylist", kind: "stringArray", label: "Zusätzliche ausgeblendete Keys", info: "Zusätzliche Settings-Schlüssel, die beim Auslesen der Brain-Settings ausgeblendet werden (einer pro Zeile). Secrets werden ohnehin automatisch entfernt.", placeholder: "customSecretKey" },
    ],
  },
  {
    title: "Observability (OpenTelemetry)",
    fields: [
      { key: "otelEnabled", kind: "bool", label: "Telemetrie aktiv", info: "Agenten exportieren OpenTelemetry (Kosten, Tokens, Tool-Aufrufe, Audit-Events) an den unten konfigurierten Collector. Aus = kein Export." },
      { key: "otelEndpoint", kind: "text", label: "OTLP-Endpoint", info: "Adresse des OpenTelemetry-Collectors, z.B. http://otel-collector:4317 (grpc) oder :4318 (http). Leer = kein Export, auch wenn aktiv." },
      { key: "otelProtocol", kind: "enum", label: "OTLP-Protokoll", info: "Übertragungsprotokoll zum Collector.", options: [{ value: "grpc", label: "gRPC (4317)" }, { value: "http/protobuf", label: "HTTP (4318)" }] },
      { key: "otelTracesEnabled", kind: "bool", label: "Distributed Tracing", info: "Zusätzlich zu Metriken/Events auch Traces exportieren (Span-Baum pro Turn — bestes Debugging für hängende Tools). Beta." },
      { key: "otelDisableAnthropicTelemetry", kind: "bool", label: "Anthropic-Telemetrie aus (Datenresidenz)", info: "Schaltet die Anthropic-EIGENE Telemetrie/Fehlerberichte/nicht-essenziellen Egress der Brains ab (DISABLE_TELEMETRY u.a.). Unabhängig vom Export oben — der geht an EUREN Collector, nicht an Anthropic." },
    ],
  },
  {
    title: "Observability — Was geloggt wird (Privacy)",
    fields: [
      { key: "otelLogToolDetails", kind: "bool", label: "Tool-Details (Bash-Kommandos, Tool-/MCP-Namen, Args)", info: "Macht in den Events das TATSÄCHLICHE Bash-Kommando + Tool-/MCP-Namen + Datei-Pfade + Argumente sichtbar — wichtig für Security-Audit + Debug (welcher Befehl lief). Kann sensible Werte enthalten → Redaktions-/Retention-Entscheidung. Default aus." },
      { key: "otelLogUserPrompts", kind: "bool", label: "User-Prompts loggen", info: "Loggt den vollen Prompt-Inhalt. Kann Quellcode, Secrets, Kunden- oder Personendaten enthalten. Nur nach Privacy-Review. Default aus." },
      { key: "otelLogToolContent", kind: "bool", label: "Tool-Output loggen", info: "Loggt den Tool-Output (Datei-Inhalt, Kommando-Ausgabe). Voller Ausführungs-Inhalt → restriktiver Speicher nötig. Default aus." },
      { key: "otelLogRawApiBodies", kind: "bool", label: "Rohe API-Bodies loggen", info: "Loggt vollständige API-Request/Response-Bodies — enthält den ganzen Konversationsverlauf. Sehr sensibel. Default aus." },
    ],
  },
  {
    title: "Audit — Aufbewahrung",
    fields: [
      { key: "auditRetentionDays", kind: "number", label: "Audit-Aufbewahrung (Tage)", info: "Die lokale Audit-DB ist nur ein schneller Cache für diese Sicht; ältere Einträge werden periodisch gekappt (die Hash-Kette bleibt via Checkpoint-Anker gültig). Die durable Langzeit-Kopie liegt in eurem Backend/SIEM (Audit-Events fließen dorthin, bevor gekappt wird). 0 = nie kappen (unbegrenzt)." },
    ],
  },
];

// #P002: Objekt-Werte (das Modell-Vokabular) stabil vergleichen — SCHLÜSSEL SORTIERT. Ohne das
// hinge der Vergleich an der Einfüge-Reihenfolge, und ein An-und-wieder-Abhaken (netto null)
// erzeugte ein Delta, das der Master als Änderung buchte.
// ⚠ Review NIT-2: `JSON.stringify(undefined, fn)` liefert zur Laufzeit `undefined`, nicht `""` —
// der Rückgabetyp sagt das jetzt auch. Für den Vergleich ist es egal (undefined === undefined),
// aber eine Signatur, die lügt, ist der Anfang des nächsten Fundes.
function stabil(v: unknown): string | undefined {
  return JSON.stringify(v, (_k, val) =>
    val && typeof val === "object" && !Array.isArray(val)
      ? Object.fromEntries(Object.entries(val as Record<string, unknown>).sort(([x], [y]) => x.localeCompare(y)))
      : val,
  );
}

// Generischer Diff über alle Keys (Arrays per join verglichen) → nur das Delta patchen.
function diff(draft: PlatformSettings, base: PlatformSettings): Partial<PlatformSettings> {
  const patch: Partial<PlatformSettings> = {};
  // ⚠ Über die Keys des DRAFTS, nicht der Basis: ein Setting, das der Master noch nicht
  // liefert (Version-Skew, hier `anthropicModelMeta`), stünde in `base` gar nicht — die
  // Eingabe daran wäre still nicht speicherbar.
  const keys = new Set<keyof PlatformSettings>([
    ...(Object.keys(base) as (keyof PlatformSettings)[]),
    ...(Object.keys(draft) as (keyof PlatformSettings)[]),
  ]);
  keys.forEach((k) => {
    const a = draft[k];
    const b = base[k];
    const changed =
      Array.isArray(a) && Array.isArray(b)
        ? a.join("\n") !== b.join("\n")
        : typeof a === "object" && a !== null
          ? stabil(a) !== stabil(b)
          : a !== b;
    if (changed) (patch as Record<string, unknown>)[k] = a;
  });
  return patch;
}

function InfoIcon({ info }: { info: string }) {
  // Natives Tooltip via title am umschließenden span (zuverlässiger als <title> im SVG).
  return (
    <span title={info} aria-label={info} className="inline-flex cursor-help">
      <Info className="h-3.5 w-3.5 text-text3" />
    </span>
  );
}

function InfoLabel({ label, info }: { label: string; info: string }) {
  return (
    <span className="mb-1 flex items-center gap-1.5 text-[12px] font-semibold text-text2">
      {label}
      <InfoIcon info={info} />
    </span>
  );
}

export function PlatformSettingsSection() {
  const query = usePlatformSettings();
  const patch = usePatchPlatformSettings();
  const [draft, setDraft] = useState<PlatformSettings | null>(null);

  useEffect(() => {
    if (query.data && draft === null) setDraft(query.data);
  }, [query.data, draft]);

  if (query.isError) {
    return (
      <SectionCard icon={<SlidersHorizontal className="h-[18px] w-[18px]" />} title="Plattform-Settings" description="Fehler">
        <div className="text-[13px] text-danger" data-testid="platform-settings-error">
          Konnte Plattform-Settings nicht laden: {(query.error as Error).message}
        </div>
      </SectionCard>
    );
  }
  if (query.isLoading || !draft) {
    return (
      <SectionCard icon={<SlidersHorizontal className="h-[18px] w-[18px]" />} title="Plattform-Settings" description="Lade…">
        <div className="h-24 animate-pulse rounded-md bg-surface2" data-testid="platform-settings-loading" />
      </SectionCard>
    );
  }

  const base = query.data!;
  const delta = diff(draft, base);
  const dirty = Object.keys(delta).length > 0;
  const d = draft;
  const set = <K extends keyof PlatformSettings>(key: K, value: PlatformSettings[K]) =>
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  async function save() {
    try {
      await patch.mutateAsync(delta);
      setDraft(null);
      toast.success("Plattform-Settings gespeichert");
    } catch (err) {
      toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  function renderField(f: Field): ReactNode {
    const disabled = patch.isPending;
    const testId = `ps-${String(f.key)}`;
    if (f.kind === "bool") {
      return (
        <label key={String(f.key)} className="flex cursor-pointer items-start gap-3">
          <input type="checkbox" checked={d[f.key] as boolean} onChange={(e) => set(f.key, e.target.checked as never)} disabled={disabled} data-testid={testId} className="mt-0.5 h-4 w-4 shrink-0 accent-accent" />
          <span className="leading-tight">
            <span className="flex items-center gap-1.5 text-[13px] font-semibold text-text">
              {f.label}
              <InfoIcon info={f.info} />
            </span>
            <span className="block text-[11.5px] text-text3">{f.info}</span>
          </span>
        </label>
      );
    }
    if (f.kind === "modelMeta") {
      // Version-Skew: liefert der Master das Setting nicht, zeigt die Maske den geschlossenen
      // Stand — und ein Klick darauf legt den Schlüssel an (der Diff sieht ihn dann).
      const meta = d.anthropicModelMeta ?? { sensitiveDataOk: false, hosting: "cloud" as const };
      return (
        <div key={String(f.key)} className="sm:col-span-2">
          <InfoLabel label={f.label} info={f.info} />
          <div className="flex flex-wrap items-center gap-4 text-[13px] text-text2">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={meta.sensitiveDataOk}
                onChange={(e) => set("anthropicModelMeta", { ...meta, sensitiveDataOk: e.target.checked })}
                disabled={disabled}
                className="h-4 w-4 accent-accent"
                data-testid="ps-anthropicModelMeta-sensitiveDataOk"
              />
              Für heikle Firmendaten geeignet
            </label>
            <label className="flex items-center gap-2">
              Betrieb:
              <select
                value={meta.hosting}
                onChange={(e) => set("anthropicModelMeta", { ...meta, hosting: e.target.value === "local" ? "local" : "cloud" })}
                disabled={disabled}
                className={INPUT_CLASS + " w-auto"}
                data-testid="ps-anthropicModelMeta-hosting"
              >
                <option value="cloud">Cloud</option>
                <option value="local">lokal</option>
              </select>
            </label>
          </div>
          <span className="mt-1 block text-[11px] text-text3">{f.info}</span>
        </div>
      );
    }
    return (
      <div key={String(f.key)}>
        <InfoLabel label={f.label} info={f.info} />
        {f.kind === "number" && (
          <input type="number" min={f.min ?? 0} step="1" value={d[f.key] as number} onChange={(e) => set(f.key, Number(e.target.value) as never)} disabled={disabled} className={INPUT_CLASS} data-testid={testId} />
        )}
        {f.kind === "text" && (
          <input type="text" value={d[f.key] as string} onChange={(e) => set(f.key, e.target.value as never)} disabled={disabled} className={INPUT_CLASS} data-testid={testId} />
        )}
        {f.kind === "enum" && (
          <select value={d[f.key] as string} onChange={(e) => set(f.key, e.target.value as never)} disabled={disabled} className={INPUT_CLASS} data-testid={testId}>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
        {f.kind === "stringArray" && (
          <textarea rows={3} value={(d[f.key] as string[]).join("\n")} onChange={(e) => set(f.key, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) as never)} disabled={disabled} placeholder={f.placeholder} className={INPUT_CLASS + " h-auto py-2 font-mono text-[12px]"} data-testid={testId} />
        )}
        <span className="mt-1 block text-[11px] text-text3">{f.info}</span>
      </div>
    );
  }

  return (
    <SectionCard
      icon={<SlidersHorizontal className="h-[18px] w-[18px]" />}
      title="Plattform-Settings"
      description="Plattform-weite Defaults + Policy-Schalter. Wirken live (nächster Turn), kein Redeploy nötig."
    >
      <div className="space-y-6" data-testid="platform-settings-form">
        {GROUPS.map((g) => (
          <div key={g.title} className="space-y-4">
            <div className="text-[11px] font-bold uppercase tracking-[.06em] text-text3">{g.title}</div>
            <div className="grid gap-4 sm:grid-cols-2">
              {g.fields.map(renderField)}
            </div>
          </div>
        ))}

        <div className="sticky bottom-0 -mx-[18px] -mb-[18px] flex items-center justify-between gap-3 border-t border-border bg-surface px-[18px] py-3">
          <Button variant="outline" size="sm" onClick={() => setDraft(base)} disabled={patch.isPending || !dirty} data-testid="ps-reset">
            <RotateCcw className="h-4 w-4" />
            Verwerfen
          </Button>
          <Button size="sm" onClick={save} disabled={patch.isPending || !dirty} data-testid="ps-save">
            <Save className="h-4 w-4" />
            Speichern{dirty ? ` (${Object.keys(delta).length})` : ""}
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}
